import type { ReactNode } from "react";
import { Input } from "./primitives";
import { cn } from "./cn";

/**
 * A labelled input with an inline error slot.
 *
 * Uses a **native `<label>`**, not Radix Label: the browser already associates a label with its
 * control and focuses it on click, and constitution VII says prefer the platform for what the
 * browser already does. That also keeps this component server-renderable — Radix Label carries
 * "use client".
 *
 * `error` is the API's message rendered verbatim where the server rejected what the client
 * allowed (data-model §5), or the client's own inline reason (FR-012).
 */
export function Field({
  label,
  name,
  error,
  hint,
  id: idOverride,
  wrapperClassName,
  required,
  ...input
}: {
  label: string;
  name: string;
  error?: string;
  hint?: ReactNode;
  /** Classes for the label+input+hint column — e.g. `col-span-full` inside a `FormGroup` grid. */
  wrapperClassName?: string;
  /**
   * Override the generated DOM id. **Required when two fields on one page share a `name`** —
   * two forms each with a `name` field would otherwise emit duplicate ids, and `htmlFor` binds
   * the label to whichever comes first (WCAG 4.1.1). Found on T153.
   */
  id?: string;
} & React.ComponentProps<typeof Input>) {
  // Not useId(): that is a client hook, and this component must stay server-renderable so a
  // <form> + Server Action page needs no client boundary (R5). `name` is unique within a form,
  // which is the same guarantee useId would buy here.
  const id = idOverride ?? `field-${name}`;
  const errorId = `${id}-error`;
  const hintId = `${id}-hint`;

  return (
    <div className={cn("flex flex-col gap-1.5", wrapperClassName)}>
      <label htmlFor={id} className="text-sm font-medium">
        {label}
        {required ? (
          <span aria-hidden="true" className="text-red">
            {" "}
            *
          </span>
        ) : null}
      </label>

      <Input
        id={id}
        name={name}
        required={required}
        aria-required={required || undefined}
        aria-invalid={error ? true : undefined}
        aria-describedby={[error ? errorId : null, hint ? hintId : null].filter(Boolean).join(" ") || undefined}
        {...input}
      />

      {hint ? (
        <p id={hintId} className="text-muted-foreground text-xs">
          {hint}
        </p>
      ) : null}

      {error ? (
        <p id={errorId} role="alert" className="mt-0.5 text-[13px] text-red">
          {error}
        </p>
      ) : null}
    </div>
  );
}
